/**
 * Etapa 24: regras da aba "Assinaturas" (sem React, para testar).
 * O Lictor não guarda os PDFs: o histórico traz só os dados de cada assinatura.
 */

export const ITENS_POR_PAGINA = 50;

/** Uma linha do histórico (GET /certificates/signatures). */
export interface SignatureEntry {
  id: string;
  signed_at: string;
  signed_by_id: string;
  signed_by_name: string | null;
  certificate_id: string | null;
  certificate_label: string | null;
  holder: string | null;
  file_name: string | null;
  purpose: string | null;
  file_size: number | null;
  sha256_signed: string | null;
  profile: string | null;
}

export interface SignatureHistory {
  items: SignatureEntry[];
  total: number;
}

interface CertificadoParaAssinar {
  active: boolean;
  can_use: boolean;
  days_to_expire: number;
}

/** Certificados que a pessoa pode usar agora: ela tem acesso, estão no Lictor e não venceram. */
export function certificadosParaAssinar<T extends CertificadoParaAssinar>(certificados: T[]): T[] {
  return certificados.filter((c) => c.can_use && c.active && c.days_to_expire >= 0);
}

/**
 * "2026-11-03T14:24:16" (UTC, com ou sem fuso) → "03/11/2026 às 11:24" no fuso do navegador.
 * Sem fuso no texto, trata como UTC (é como o servidor grava).
 */
export function formatarDataHora(iso: string, fuso?: string): string {
  const comFuso = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`;
  const data = new Date(comFuso);
  if (Number.isNaN(data.getTime())) return iso;
  const partes = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: fuso,
  }).formatToParts(data);
  const parte = (tipo: string) => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('day')}/${parte('month')}/${parte('year')} às ${parte('hour')}:${parte('minute')}`;
}

/** 1536 → "1,5 KB"; 2_500_000 → "2,4 MB". */
export function formatarTamanho(bytes: number | null): string | null {
  if (bytes === null || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
  return `${(kb / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

/** Monta a query do histórico: só manda o que foi preenchido. */
export function consultaDoHistorico(busca: string, deslocamento: number, limite = ITENS_POR_PAGINA): string {
  const parametros = new URLSearchParams({ limit: String(limite), offset: String(deslocamento) });
  const termo = busca.trim();
  if (termo) parametros.set('q', termo);
  return `?${parametros.toString()}`;
}
